import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ExerciseService, ExercisesService, HelperService, IExerciseFilters, IExerciseFiltersMultiple, OrganizationService, SearchService, StorageService, UserService } from 'src/app/services';
import { Exercise, Organization } from 'src/app/models';
import { isEqual, safeFindIndex } from 'src/app/utils';

interface localExerciseFilters extends IExerciseFiltersMultiple {
  sortBy: "exercises_elitetrainr" | "exercises_counter_elitetrainr";
}

@Component({
  selector: 'app-exercise-select',
  templateUrl: './exercise-select.page.html',
  styleUrls: ['./exercise-select.page.scss'],
})
export class ExerciseSelectPage implements OnInit {


  public activeFilters: localExerciseFilters = {
    equipment: [],
    exerciseType: [],
    bodyPart: [],
    status: "active",
    type: "system",
    sortBy: "exercises_counter_elitetrainr"
  };
  public activeIndex: "exercises_elitetrainr" | "exercises_counter_elitetrainr" = "exercises_counter_elitetrainr";
  public algoliaConfig =  {
    indexName: 'exercises_elitetrainr',
    //searchClient
  };
  public algoliaCounterConfig =  {
    indexName: 'exercises_counter_elitetrainr',
   // searchClient
  };
  public algoliaParams: any = {
    hitsPerPage: 10,
    page: 0
  }
  public hasMore = true;

  public bodyParts: any[];
  public hits: any;
  public equipment: any[];
  public exerciseTypes: any[] = [];
  public filteredExercises: any[] = [];
  public loading: boolean = true;
  public options = {
    hideStatus: true,
    mode: null,
  };
  public organization: Organization;
  public previousBodyParts: string[];
  public recentExercises: any[];
  public recentExercisesLoaded = false;
  public searchTerm = "";
  public selectedExercises = [];

  @Input() equipmentList: {[equipmentValue: string]: "available" | "unavailable"};
  @Input() filterOptions: any;
  @Input() showNav = true;
  @Input() multiple = false;
  @Input() userId: string;

  constructor(
    private exerciseService: ExerciseService,
    private helperService: HelperService,
    private modalCtrl: ModalController,
    private organizationService: OrganizationService,
    private searchService: SearchService,
    private storageService: StorageService,
    private userService: UserService,
  ) {
    this.organization = this.organizationService.getAppOrganization();
  }

  ngOnInit() {
   
  }

  async ionViewDidEnter() {
    await this.initializeData();

    if(this.filterOptions) {
      this.options = Object.assign(this.options, this.filterOptions);
      if(this.options.mode === "cardio") {
        this.activeFilters.exerciseType.push("cardio");
      }
      if(this.options.mode === "training") {
        // Default workout tracking to training exercises (exclude cardio).
        this.activeFilters.exerciseType = ["training"];
      }

      // Allow callers to pre-filter by bodyPart (e.g. split tracking).
      const bodyPartOpt = this.filterOptions?.bodyPart;
      const bodyPartsArray: string[] = Array.isArray(bodyPartOpt)
        ? bodyPartOpt
        : (bodyPartOpt ? [bodyPartOpt] : []);

      if(bodyPartsArray.length > 0) {
        const expanded: string[] = [];
        for (const part of bodyPartsArray) {
          if(!part) continue;
          if(expanded.indexOf(part) === -1) expanded.push(part);
          const children = this.helperService.getChildrenValues(part, this.bodyParts);
          if(children?.length > 0) {
            for (const child of children) {
              if(expanded.indexOf(child) === -1) expanded.push(child);
            }
          }
        }
        this.activeFilters.bodyPart = expanded;
        this.previousBodyParts = expanded;
      }
    } else {
      const activeFilters = await this.storageService.get("exerciseSelectFilters");
      if(activeFilters) {
        Object.assign(this.activeFilters, activeFilters);
        if(activeFilters.sortBy) {
          this.activeIndex = this.activeFilters.sortBy;
        }
      }
    }
    // These are fallbacks for users who have "all" stored locally.
    // @ts-ignore: Unreachable code error
    this.activeFilters.bodyPart = this.activeFilters.bodyPart === "all"? [] : this.activeFilters.bodyPart;
    this.previousBodyParts = this.activeFilters.bodyPart;
    // @ts-ignore: Unreachable code error
    this.activeFilters.exerciseType = this.activeFilters.exerciseType === "all"? [] : this.activeFilters.exerciseType;
    // @ts-ignore: Unreachable code error
    this.activeFilters.equipment = this.activeFilters.equipment === "all"? [] : this.activeFilters.equipment;


    this.setFilterType(this.activeFilters.type, true);

    try {
      if (this.activeFilters.type === "recents") {
        if (!this.recentExercisesLoaded) {
          await this.loadRecentlyUsedExercises();
        }
      } else {
        await this.setAlgoliaFilters();
      }
    } finally {
      this.loading = false;
    }
  }

  initializeData() {
    const promises = [];
    promises.push(this.loadExerciseTypes());
    promises.push(this.loadBodyParts());
    promises.push(this.loadEquipment());
    return Promise.all(promises);
  }

  async loadExerciseTypes() {
    this.exerciseTypes = await this.exerciseService.getSystemExerciseTypes();
  }

  async loadBodyParts() {
    this.bodyParts = await this.exerciseService.getSystemBodyParts()
  }

  async loadEquipment() {
    this.equipment = await this.exerciseService.getSystemEquipment();
  }

  async loadRecentlyUsedExercises() {
    const exercisesObj = await this.exerciseService.getRecentlyUsedExercises(this.userService.getAppUser().uid);
    if(exercisesObj) {
      const exerciseIds = Object.keys(exercisesObj);
      const promises = [];
      const exercises = [];
      for (const id of exerciseIds) {
        promises.push(this.exerciseService.getExerciseAsPromise(id).then((exercise) => {
          if(exercise) {
            exercises.push(exercise);
          }
        }));
      }
      await Promise.all(promises);
      if(exercises.length > 0) {
        this.recentExercises = exercises;
      }
    }
    this.recentExercisesLoaded = true;
  }

  resetLimit() {
    this.algoliaParams.hitsPerPage = 10;
    this.algoliaParams.page = 0;
    this.hasMore = true;
  }

  async loadData(event) {
    if (this.activeFilters.type === "recents") {
      event.target.complete();
      return;
    }
    this.algoliaParams.page = (this.algoliaParams.page || 0) + 1;
    await this.setAlgoliaFilters({ append: true });
    event.target.complete();
  }

  storeActiveFilters() {
    this.storageService.set("exerciseSelectFilters", this.activeFilters)
  }

  filterChanged(event?) {
    setTimeout(() => {
      // Check if items were added
      if(this.activeFilters.bodyPart?.length > this.previousBodyParts.length) {
        let difference = this.activeFilters.bodyPart.filter(x => !this.previousBodyParts.includes(x));
        if(difference.length > 0) {
          const items = [...this.activeFilters.bodyPart];
          for (const part of difference) {
            const children = this.helperService.getChildrenValues(part, this.bodyParts);
            if(children?.length > 0) {
              for (const child of children) {
                if(items.indexOf(child) === -1) {
                  items.push(child);
                }
              }
            };
          }
          this.activeFilters.bodyPart = items;
        }
      }

      // Check if items were removed
      if(this.activeFilters.bodyPart?.length < this.previousBodyParts.length) {
        let difference = this.previousBodyParts.filter(x => !this.activeFilters.bodyPart.includes(x));
        if(difference.length > 0) {
          const items = [...this.activeFilters.bodyPart];
          for (const part of difference) {
            const children = this.helperService.getChildrenValues(part, this.bodyParts);
            if(children?.length > 0) {
              for (const child of children) {
                const index = items.indexOf(child);
                if(index > -1) {
                  items.splice(index, 1);
                }
              }
            };
          }
          this.activeFilters.bodyPart = items;
        }
      }
      this.previousBodyParts = this.activeFilters.bodyPart || [];

      this.resetLimit();
      this.setAlgoliaFilters();
      this.storeActiveFilters();
    }, 5);

  }

  selectAll(filter, category) {
    const items = this.activeFilters[filter]? [...this.activeFilters[filter]] : [];
    const index = items.indexOf(category);
    if(index === -1) {
      items.push(category)
    }

    let toSearch;
    if(filter === "equipment") {
      toSearch = this.equipment;
    } else if(filter === "bodyPart") {
      toSearch = this.bodyParts;
      this.previousBodyParts = this.activeFilters.bodyPart;
    }

    const children = this.helperService.getChildrenValues(category, toSearch);
    if(children && children.length > 0) {
      for (const child of children) {
        const childIndex = items.indexOf(child);
        if(childIndex === -1) {
          items.push(child);
        }
      }
    }

    setTimeout(() => {
      this.activeFilters[filter] = items;
      this.filterChanged();
    }, 50);
  }

  clearAll(filter, category) {
    const items = this.activeFilters[filter]? [...this.activeFilters[filter]] : null;
    if(items && items.length > 0) {
      const index = items.indexOf(category);
      if(index > -1) {
        items.splice(index, 1)
      }

      let toSearch;
      if(filter === "equipment") {
        toSearch = this.equipment;
      } else if(filter === "bodyPart") {
        toSearch = this.bodyParts;
        this.previousBodyParts = this.activeFilters.bodyPart;
      }

      const children = this.helperService.getChildrenValues(category, toSearch);
      if(children && children.length > 0) {
        for (const child of children) {
          const childIndex = items.indexOf(child);
          if(childIndex > -1) {
            items.splice(childIndex, 1)
          }
        }
      }
    }

    setTimeout(() => {
      this.activeFilters[filter] = items;
      this.filterChanged();
    }, 50);
  }

  indexChange() {
    this.activeFilters.sortBy = this.activeIndex;
    this.storeActiveFilters();
    this.resetLimit();
    this.setAlgoliaFilters();
  }

  setFilterType(type, skipSearch = false) {
    this.activeFilters.type = type;
    this.resetLimit();
    this.storeActiveFilters();
    if (skipSearch) {
      return;
    }
    if (this.activeFilters.type === "recents") {
      if (!this.recentExercisesLoaded) {
        this.loadRecentlyUsedExercises()
      }
    } else {
      this.setAlgoliaFilters();
    }
  }

  escapeDash(string: string) {
    return string.charAt(0) === '-'? '\\' + string : string
  }

  async setAlgoliaFilters(opts?: { append?: boolean }) {
    this.algoliaParams.query = this.activeFilters.searchTerm || "";
    let filters = [];

    filters.push(`organization:${this.activeFilters.type === "system"? 'system' : this.escapeDash(this.organization.uid)}`);
    filters.push(`status:active`);

    // Chec Bodyparts
    if(this.activeFilters.bodyPart && this.activeFilters.bodyPart.length > 0) {
      const bodyParts = [];
      for (const check of this.activeFilters.bodyPart) {
        bodyParts.push(`bodyParts:${check}`)
      }
      filters.push(bodyParts);
    }

    // Check Equipment
    if(this.activeFilters.equipment && this.activeFilters.equipment.length > 0) {
      const equipment = [];
      for (const check of this.activeFilters.equipment) {
        equipment.push(`equipment:${check}`)
      }
      filters.push(equipment);
    }

    // Check Exercise Type Type
    if(this.activeFilters.exerciseType && this.activeFilters.exerciseType.length > 0) {
      for (const ET of this.activeFilters.exerciseType) {
        filters.push(`exerciseTypes:${ET}`);
      }
    }
    this.algoliaParams.facetFilters = filters;
    this.algoliaParams.page = opts?.append ? this.algoliaParams.page : 0;

    const results = await this.searchService.search(this.activeIndex, this.algoliaParams, "exercise-select");
    if (!results) {
      return;
    }
    if (opts?.append) {
      this.hits = [...(this.hits || []), ...(results.hits || [])];
    } else {
      this.hits = results?.hits
    }
    const page = results.page ?? this.algoliaParams.page ?? 0;
    const nbPages = results.nbPages ?? 0;
    this.hasMore = page + 1 < nbPages;
  }

  isExerciseSelected(foodId) {
    return safeFindIndex(this.selectedExercises, e => e.id === foodId) > -1
  }

  toggleExercise(exercise) {
    if (this.isExerciseSelected(exercise.id)) {
      this.removeExercise(exercise.id);
    } else {
      this.addExercise(exercise);
    }
  }

  selectExercise(exercise) {
    if(this.multiple) {
      this.toggleExercise(exercise);
    } else {
      this.exerciseService.updateExerciseCounter(exercise.id).then(() => {}).catch(() => {});
      this.exerciseService.saveRecentlyUsedExercise(this.userService.getAppUser().uid, exercise.id).then(() => {}).catch(() => {});
      this.modalCtrl.dismiss({ status: "selected", data: exercise.id, exercise: this.helperService.copyObject(exercise) }, "selected");
    }
  }

  addExercise(exercise) {
    this.selectedExercises.push(exercise);
  }

  removeExercise(foodId) {
    const index = safeFindIndex(this.selectedExercises, e => e.id === foodId);
    if (index > -1) {
      this.selectedExercises.splice(index, 1);
    }
  }

  done() {
    const promises = [];
    for (const exer of this.selectedExercises) {
      promises.push(this.exerciseService.updateExerciseCounter(exer.id));
      promises.push(this.exerciseService.saveRecentlyUsedExercise(this.userService.getAppUser().uid, exer.id));
    }
    Promise.all(promises).then(() => {}).catch(() => {});
    this.modalCtrl.dismiss({
      exercises: this.selectedExercises.map((exercise) => {return new Exercise(exercise)}), 
      filters: this.helperService.copyObject(this.activeFilters)
    }, "selected");
  }

  cancel() {
    this.modalCtrl.dismiss({ status: "canceled" });
  }
}
